import { defineStore } from "pinia";
import { BookingsApi, RequestsApi } from "@simbelapp/order-sdk";
import { useTrainingPlayerStore } from "../training-player/training-player.store";
import { useTrainingsStore } from "../trainings/trainings.store";
import { useSessionsStore } from "../sessions/sessions-store";
import { useAuthStore } from "~/store/auth/auth.store";
import { useFeedback } from "~/composables/feedback/useFeedback";
import { useRequestsValidation } from "~/composables/requests/useRequests";
import { useOffCatalogRequests } from "~/store/catalog/off-catalog-requests.store";
import { RequestStep, type ICreateRequest } from "~/utils/interfaces/requests-interfaces";
import { BookingStatusEnum } from "~/utils/enums/bookings-events.enums";
import { MicroserviceEnum } from "~/utils/enums/common.enums";
import { useSDKApi } from "~/composables/api/useSDKApi";
import { isSessionFullError, SESSION_FULL_MESSAGE } from "~/utils/helpers/sessions/session-capacity";

export const useCreateRequestStore = defineStore("createRequestStore", {
  state: (): ICreateRequest => {
    return {
      request: {
        trainingId: null,
        sessionId: null,
        usersToInvite: [],
        employeeComment: null,
        managerPriority: null,
        managerComment: null,
        isUnqualified: false,
      },
      nav: null,
      isCheckedForMe: false,
      isCheckedForOther: false,
      showModal: false,
      showExit: false,
      showConfirm: false,
      usersError: null,
      employeeCommentError: null,
      managerPriorityError: null,
      radioError: null,
      stepModal: null,
      noSessionSelected: false,
      isDesktop: false,
      choiceSessionError: false,
      usersAlreadyRegisteredModal: false,
      usersAlreadyRegisteredData: [],
    };
  },
  getters: {
    checkCreateOtherRequestError(): boolean {
      if (this.employeeCommentError || this.managerPriorityError || this.radioError || this.usersError) {
        return true;
      } else {
        return false;
      }
    },
  },
  actions: {
    openRequestModal() {
      this.resetStore();
      this.showModal = true;
    },
    resetStore() {
      const saveSession = this.request.sessionId;
      const saveTrainingId = this.request.trainingId;
      this.$reset();
      this.request.sessionId = saveSession;
      this.request.trainingId = saveTrainingId;
    },
    /**
     * Clears what the request points at. `createEmployeeRequest` derives `isUnqualified` from these
     * two keys, so an "I don't know the training" answer has to go through here.
     */
    clearTrainingSelection() {
      this.request.sessionId = null;
      this.request.trainingId = null;
    },
    resetRequest() {
      this.request.usersToInvite = [];
      this.request.employeeComment = null;
      this.request.managerPriority = null;
      this.request.managerComment = null;
    },
    resetRequestError() {
      this.employeeCommentError = null;
      this.managerPriorityError = null;
      this.usersError = null;
    },
    async validateManagerRequestCreation() {
      const requestsValidation = useRequestsValidation();
      const t$ = await requestsValidation.validateManagerRequestsCreation();

      if (t$.value.$errors) {
        this.usersError = t$.value.usersToInvite?.$silentErrors.map((m: any) => m.$message).join("") ?? "";
        this.employeeCommentError = t$.value.employeeComment?.$silentErrors.map((m) => m.$message).join("") ?? "";
        this.managerPriorityError = t$.value.managerPriority?.$silentErrors.map((m) => m.$message).join("") ?? "";
        // this.radioError = t$.value.isCheckedForOther?.$silentErrors.map((m: any) => m.$message).join("") ?? "";
      }
    },
    async validateRequestCreation() {
      const requestsValidation = useRequestsValidation();
      const t$ = await requestsValidation.validateRequestsCreation();
      if (t$.value.$errors) {
        this.employeeCommentError = t$.value.employeeComment?.$silentErrors.map((m) => m.$message).join("") ?? "";
      }
    },

    async createRequest(isOther: boolean = false) {
      const authStore = useAuthStore();
      if (
        ((authStore.isManager || authStore.isCompanyAdmin || authStore.isGroupViewer || authStore.isGroupAdmin) &&
          this.isDesktop) ||
        isOther
      ) {
        return await this.createOtherRequest();
      } else {
        return await this.createEmployeeRequest();
      }
    },

    async createBooking(trainingId: string, sessionId?: string) {
      const feedback = useFeedback();
      const authStore = useAuthStore();
      const useTrainingPlayer = useTrainingPlayerStore();
      // Aliased: `this.showModal` right below is the request modal, this one is the LMS player.
      const { showModal: showPlayerModal } = storeToRefs(useTrainingPlayer);
      const trainingsStore = useTrainingsStore();
      const { trainingPageDetails } = storeToRefs(trainingsStore);

      try {
        const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
        const createBookings = await apiInstance.createBooking({
          createBookingsDto: {
            trainingId,
            sessionId,
            bookingsStatus: BookingStatusEnum.BOOKING_CONFIRMED,
            usersToInvite: this.request.usersToInvite.length
              ? this.request.usersToInvite.map((user) => ({ userId: user.userId }))
              : authStore.user?.userId
                ? [{ userId: authStore.user.userId }]
                : [],
          },
        });

        // An empty `invites` means the backend created NOTHING — it still answers 201 with
        // `message: "bookings created"` and a `session_id`, and it never raises "Users already invited
        // to this session", so the catch below is dead for this case and the status code tells us
        // nothing. Without this guard the flow reported success and navigated to
        // `/my-trainings/undefined` for a booking that does not exist.
        //
        // Three things empty it, and the response carries no way to tell them apart (no skipped list,
        // no reason) — see `booking-create.service.ts` / `booking-create-orchestrator.service.ts` in
        // Orders:
        //   1. no attendee in the payload ("createBookings: no attendees provided");
        //   2. every requested user already has an active booking on this session (the dominant case);
        //   3. no requested id resolves to a user whose `mainEntityId` is the session's company.
        //
        // So the copy only asserts "already registered" where that is the ONLY reachable cause: for
        // yourself, (1) means no logged-in user and (3) means you are browsing another company's
        // catalog. With several invitees a single stale or cross-company id lands on (3), so that
        // message stays factual instead of guessing. Getting a precise message back — and reviving the
        // `RequestAlreadyRegisterModal` branch below, unreachable for the same reason — needs the
        // backend to return the skipped users with a reason.
        if (!createBookings.invites?.length) {
          const invitees = this.request.usersToInvite;
          const onlySelf =
            authStore.isOnlyEmployee ||
            !invitees.length ||
            (invitees.length === 1 && invitees[0].userId === authStore.user?.userId);

          if (onlySelf) {
            feedback.error(`Vous êtes déjà inscrit à cette session`, "small");
          } else if (invitees.length === 1) {
            feedback.error(`La personne est déjà inscrite à cette session`, "small");
          } else {
            feedback.error(`Aucune inscription n'a été créée pour les personnes sélectionnées`, "small");
          }
          return;
        }

        // if only role = employee, redirect to my-trainings
        if (authStore.isOnlyEmployee) {
          feedback.success(`Votre inscription a bien été prise en compte`, "small");
          this.showModal = false;
          const bookingId: string = createBookings.invites[0];
          navigateTo(`/my-trainings/${bookingId}`);

          if (trainingPageDetails.value?.isLms) {
            showPlayerModal.value = true;
          }
          return;
        }
        // If only own user is invited, redirect to my-trainings
        if (this.request.usersToInvite.length === 1) {
          if (this.request.usersToInvite[0].userId === authStore.user?.userId) {
            feedback.success(`Votre inscription a bien été prise en compte`, "small");
            this.showModal = false;
            const bookingId: string = createBookings.invites[0];
            navigateTo(`/my-trainings/${bookingId}`);

            if (trainingPageDetails.value?.isLms) {
              showPlayerModal.value = true;
            }
          } else {
            feedback.success(`Votre inscription a bien été prise en compte`, "small");
            this.showModal = false;
          }
        } else {
          // `invites` holds ONLY the bookings actually created. Orders skips attendees who already
          // have an active booking on this session and enrols the rest
          // (`booking-create-orchestrator.service.ts` filters `userToAddIds`), so a partly-fulfilled
          // batch answers 201 with a NON-EMPTY array — the empty-invites guard above never sees it.
          // Reporting "les utilisateurs ont bien été inscrits" there hides everyone who was dropped.
          //
          // Comparing counts is enough to detect it, and needs nothing from the backend. Naming WHO
          // was dropped (and why) does — same contract gap as the guard above.
          const requested = this.request.usersToInvite.length;
          const created = createBookings.invites.length;

          if (created < requested) {
            feedback.error(
              created === 1
                ? `1 personne sur ${requested} a été inscrite`
                : `${created} personnes sur ${requested} ont été inscrites`,
              "small",
            );
          } else {
            feedback.success(`Les utilisateurs ont bien été inscrits `, "small");
          }
          this.showModal = false;
        }
      } catch (error: any) {
        if (isSessionFullError(error)) {
          feedback.error(SESSION_FULL_MESSAGE, "small");
          return;
        }
        if (error.message === "Users already invited to this session" && authStore.isOnlyEmployee) {
          feedback.error(`Vous êtes déjà inscrit à cette session`, "small");
          return;
        }
        if (
          error.message === "Users already invited to this session" &&
          this.request.usersToInvite.length === 1 &&
          error?.data?.length === 1 &&
          error?.data.find((booking: any) => booking.userId === this.request.usersToInvite[0].userId)
        ) {
          feedback.error(`La personne est déjà inscrite à cette session`, "small");
          return;
        }
        // if error for other users, open modal RequestAlreadyRegisterModal with props as data
        else if (error.message === "Users already invited to this session" && this.request.usersToInvite.length > 1) {
          if (
            this.request.sessionId?.maxNbAttendees <= this.request.sessionId?.nbAttendees &&
            (this.request.sessionId?.overbookingNotAllowed || this.request.sessionId?.overBookingNotAllowed)
          ) {
            this.stepModal = RequestStep.AddComment;
            this.choiceSessionError = false;
            return;
          }

          this.usersAlreadyRegisteredData = error.data;
          this.usersAlreadyRegisteredModal = true;
          return;
        }

        feedback.error(`Erreur lors de la confirmation de l'inscription`, "small");
      } finally {
        if (trainingPageDetails.value?.trainingId) {
          const useSessions = useSessionsStore();
          await useSessions.fetchSessionsByTrainingId({
            trainingId: trainingPageDetails.value?.trainingId,
            withProject: "false",
            inProgress: "false",
          });
        }
      }
    },
    async createEmployeeRequest() {
      const authStore = useAuthStore();
      const feedback = useFeedback();
      const offCatalogRequests = useOffCatalogRequests();

      await this.validateRequestCreation();
      if (authStore.user?.userId && this.request.employeeComment && !this.employeeCommentError) {
        if (offCatalogRequests?.training?.training) {
          offCatalogRequests.training.employeeComment = this.request.employeeComment;
          await offCatalogRequests.createOffCatalogRequest();
        } else {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          const data = await apiInstance.internalCreateRequest({
            createRequestDto: {
              trainingId: this.request.sessionId ? null : this.request.trainingId,
              sessionId: this.request.sessionId?.sessionId,
              usersId: [authStore.user.userId],
              employeeComment: this.request.employeeComment,
              isUnqualified: !(this.request.sessionId || this.request.trainingId),
            },
          });
          if (data) {
            this.showModal = false;
            this.request.sessionId = null;
            navigateTo(`/my-trainings/${data.requestIds[0]}`);
            feedback.success("Votre demande a bien été enregistrée", "small");
          } else {
            feedback.error(`Une erreur est survenue lors de l'ajout`, "small");
          }

          return data;
        }
      }
    },

    async createOtherRequest() {
      const authStore = useAuthStore();
      const feedback = useFeedback();
      const offCatalogRequests = useOffCatalogRequests();

      await this.validateManagerRequestCreation();

      if (
        (this.request.usersToInvite || authStore.user?.userId) &&
        this.request.employeeComment &&
        !this.checkCreateOtherRequestError
      ) {
        if (offCatalogRequests?.training?.training) {
          offCatalogRequests.training.employeeComment = this.request.employeeComment;
          await offCatalogRequests.createOffCatalogRequest();
        } else if (authStore.user?.userId) {
          const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, RequestsApi);
          const data = await apiInstance.internalCreateSpecificRequest({
            createRequestDto: {
              trainingId: this.request.sessionId ? null : this.request.trainingId,
              sessionId: this.request.sessionId?.sessionId,
              usersId: this.request.usersToInvite.length
                ? this.request.usersToInvite?.map((u) => u.userId)
                : [authStore.user?.userId],
              employeeComment: this.request.employeeComment,
              managerId: authStore.user?.userId ?? null,
              ...(this.request.managerPriority && {
                managerPriority: this.request.managerPriority?.key,
              }),
              ...(this.request.managerComment && {
                managerComment: this.request.managerComment,
              }),
              isUnqualified: !(this.request.sessionId || this.request.trainingId),
            },
          });

          this.isCheckedForMe = !this.request.usersToInvite.some((u) => u.userId !== authStore.user?.userId);

          // The modal is closed only on success (below). It used to be closed here too, before `data`
          // was checked, so a failed creation still dismissed the form and fired its error toast
          // behind a closed modal — the failure was indistinguishable from a success, and the user
          // lost everything they had typed.
          if (data) {
            this.showModal = false;

            if (
              this.request.usersToInvite.find((u) => u.userId === authStore.user?.userId) &&
              this.request.usersToInvite.length === 1
            ) {
              navigateTo(`/my-trainings/${data.requestIds[0]}`);
              feedback.success("Votre demande a bien été enregistrée", "small");
            } else if (
              this.request.usersToInvite.length > 1 ||
              this.request.usersToInvite.find((u) => u.userId !== authStore.user?.userId)
            ) {
              this.showConfirm = true;
            }
          } else {
            feedback.error(`Une erreur est survenue lors de l'ajout`, "small");
          }

          return data;
        }
      }
    },
    async confirmUsersAlreadyRegistered(trainingId: string, sessionId: string) {
      const apiInstance = await useSDKApi(MicroserviceEnum.ORDERS, BookingsApi);
      const feedback = useFeedback();
      const usersToInvite = this.request.usersToInvite?.filter(
        (user) => !this.usersAlreadyRegisteredData?.some((booking: any) => booking.userId === user.userId),
      );

      try {
        if (usersToInvite.length) {
          await apiInstance.createBooking({
            createBookingsDto: {
              sessionId,
              trainingId,
              bookingsStatus: BookingStatusEnum.BOOKING_CONFIRMED,
              usersToInvite: usersToInvite.map((user) => ({ userId: user.userId })),
            },
          });
        }
        feedback.success(`Les utilisateurs ont bien été inscrits `, "small");
      } catch (error: any) {
        feedback.error(
          isSessionFullError(error) ? SESSION_FULL_MESSAGE : `Une erreur est survenue lors de l'inscription`,
          "small",
        );
      }
    },
  },
});
